import { useCallback } from "react";
import {
  Controller,
  DefaultValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { CloseOutlined } from "@mui/icons-material";
import { Box, Button, Grid, InputAdornment, Stack } from "@mui/material";
import {
  FieldOwnerState,
  MultiInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import __ from "src/utils/Translations";
import {
  FilterFormSchema,
  FilterModel,
} from "src/Pages/Company/Pending/PendingBenefits/types";
import { EmployeesAutocomplete } from "src/Pages/Company/Pending/PendingBenefits/Filters/EmployeesAutocomplete";
import { isEqual } from "lodash";
import { OrganizationUnitsAutocomplete } from "./OrganizationUnitsAutocomplete";
import { StatusAutocomplete } from "./StatusAutocomplete";
import { BarcodeIdsField } from "./BarcodeIdsField";
import { BenefitNameField } from "./BenefitNameField";
import { prepareFilters } from "./utils";

export const PendingBenefitsFilters = ({
  filters = {},
  setFilters,
  refetch,
}: {
  filters: FilterModel;
  setFilters?: React.Dispatch<React.SetStateAction<FilterModel>>;
  refetch?: VoidFunction;
}) => {
  const methods = useForm<FilterFormSchema>({
    defaultValues,
  });

  const onValid = useCallback<SubmitHandler<FilterFormSchema>>(
    (payload) => {
      const newFilters = prepareFilters(payload);
      if (isEqual(filters, newFilters)) {
        refetch?.();
      }
      setFilters?.(newFilters);
    },
    [filters, refetch, setFilters]
  );

  const handleResetFilters = useCallback(() => {
    methods.reset(defaultValues);
    const emptyFilters = prepareFilters({});
    if (isEqual(filters, emptyFilters)) {
      refetch?.();
    }
    setFilters?.(emptyFilters);
  }, [filters, methods, refetch, setFilters]);

  return (
    <FormProvider {...methods}>
      <Box sx={{ py: 1 }} component="form" noValidate autoComplete="off">
        <Grid container spacing={2}>
          <Grid size={12}>
            <Controller
              name="barcodeIds"
              control={methods.control}
              render={(params) => <BarcodeIdsField {...params} />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="date"
              control={methods.control}
              render={({
                field: { ref, value, onChange },
                fieldState: { invalid, error },
              }) => (
                <DateRangePicker
                  label={__("Data")}
                  value={value || [null, null]}
                  onChange={onChange}
                  slots={{ field: MultiInputDateRangeField }}
                  slotProps={{
                    field: {
                      slots: {
                        separator: () => null, // Removes the separator component
                      },
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any,
                    textField: ({
                      position,
                    }: FieldOwnerState & { position?: "start" | "end" }) => ({
                      label:
                        position === "start" ? __("Data od") : __("Data do"),
                      error: invalid,
                      helperText: error?.message,
                      fullWidth: true,
                      inputRef: position === "start" ? ref : undefined,
                      InputProps: {
                        endAdornment: (
                          <InputAdornment
                            sx={{ cursor: "pointer" }}
                            position="end"
                          >
                            {position === "start" && value?.[0] && (
                              <CloseOutlined
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onChange([null, value?.[1]]);
                                }}
                              />
                            )}
                            {position === "end" && value?.[1] && (
                              <CloseOutlined
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onChange([value?.[0], null]);
                                }}
                              />
                            )}
                          </InputAdornment>
                        ),
                      },
                    }),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <StatusAutocomplete
              name="conditionType"
              label={__("Status/Akcja")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <EmployeesAutocomplete
              name="employee"
              label={__("Pracownik/Numer FK")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <OrganizationUnitsAutocomplete
              name="organizationUnitId"
              label={__("Jednostka organizacyjna")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <BenefitNameField />
          </Grid>
        </Grid>
        <Stack
          direction="row"
          sx={{ justifyContent: "flex-end", gap: 1, mt: 1 }}
        >
          <Button variant="outlined" type="button" onClick={handleResetFilters}>
            {__("Wyczyść filtry")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={methods.handleSubmit(onValid)}
          >
            {__("Szukaj")}
          </Button>
        </Stack>
      </Box>
    </FormProvider>
  );
};

const defaultValues: DefaultValues<FilterFormSchema> = {
  employee: null,
  date: [null, null],
  benefitName: null,
  organizationUnitId: null,
  conditionType: undefined,
  barcodeIds: [],
};
